import { type CMSLinkUrlDTO, type CMSQuoteDTO } from "../dto";
import { CURRENT_LANGUAGE, DEFAULT_LANGUAGE } from "../../utils/constants";
import { Button } from "~/common/components/Button/Button";
import { makeExternallyStyledBaseElement, makeExternallyStyledElement } from "../CMSExternalStyleHelper";
import { styled } from "@mui/material";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import { C_ON_SURFACE, C_SURFACE } from "~/common/components/icon/Colors";

export interface CMSQuoteProps extends Omit<CMSQuoteDTO, "type"> {
  className?: string;
}

export const CMSQuote = (props: CMSQuoteProps) => {
  const url = selectBestUrl(props.linkUrl);

  return (
    <Root rootStyles={props.rootStyles}>
      <div className={"row d-flex flex-wrap"}>
        <div className={"col-12 col-sm-7 col-md-8 scha__cmsquote__quote-div-pl-20"}>
          <StyledQuote rootStyles={props.quoteStyles}>{props.quote}</StyledQuote>
        </div>
        <div className={"row col-12 col-sm-5 col-md-4 ml-2 scha__cmsquote__div-image"}>
          <div className={"col-2 col-sm-4 scha__cmsquote__div-min-width"}>
            <img className={"scha__cmsquote__img"} src={props.avatarUrl} />
          </div>
          <div className={"col-5 col-md-7 mt-3"}>
            <StyledName rootStyles={props.nameStyles}>{props.name}</StyledName>
            <StyledSubtitle rootStyles={props.subtitleStyles}>{props.subtitle}</StyledSubtitle>
          </div>
        </div>
      </div>
      <div className={"row d-flex flex-wrap"}>
        <div className={"col-12 col-sm-2 col-md-2 col-lg-2"}>
          {props.btnLabel != "" && !!url && (
            <Button
              fullWidth={true}
              className={"button scha__teaser__button mt-4"}
              href={url}
              data-test-id={"quote-button-navigation"}
              target={props.target === "newWindow" ? "_blank" : undefined}
            >
              {props.btnLabel}
            </Button>
          )}
        </div>
      </div>
    </Root>
  );
};

const Root = makeExternallyStyledBaseElement("div");

const Quote = styled("span")({
  boxShadow: `10px 0 0 ${C_SURFACE}, -10px 0 0 ${C_SURFACE}`,
  boxDecorationBreak: "clone",
  color: C_ON_SURFACE,
  backgroundColor: C_SURFACE,
  ...getFontStyle(FontSize.DisplaySmall),
  fontStyle: "italic",
});
const StyledQuote = makeExternallyStyledElement(Quote);

const Name = styled("div")({
  ...getFontStyle(FontSize.TitleMedium),
  color: C_ON_SURFACE,
});

const Subtitle = styled("div")({
  ...getFontStyle(FontSize.TitleSmall),
  color: C_ON_SURFACE,
});

const StyledName = makeExternallyStyledElement(Name);
const StyledSubtitle = makeExternallyStyledElement(Subtitle);

const selectBestUrl = (links?: CMSLinkUrlDTO[]) => {
  if (!links || links.length === 0) {
    return undefined;
  }

  const findByLng = (lng: string) => (links: CMSLinkUrlDTO[]) =>
    links.find((item) => item.language.toLowerCase() === lng);

  const bestMatch =
    findByLng(CURRENT_LANGUAGE.toLowerCase())(links) ||
    findByLng(DEFAULT_LANGUAGE.toLowerCase())(links) ||
    findByLng("en")(links) ||
    links[0];

  return bestMatch.url;
};
